import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../components/ui/table';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { toast } from 'sonner';
import { employeeAPI } from '../../lib/api';
import { FileDown, Upload, X, AlertCircle, Loader2 } from 'lucide-react';

const CSV_HEADER = [
    'employee_code', 'first_name', 'last_name', 'email', 'phone', 'date_of_joining', 'user_password',
    'pan', 'aadhaar', 'account_number', 'bank_name', 'ifsc_code', 'branch', 'pf_account_number', 'uan'
];

const HEADER_MAPPING = {
    'employee_id': 'employee_code',
    'emp_code': 'employee_code',
    'first_name': 'first_name',
    'last_name': 'last_name',
    'email_address': 'email',
    'phone_number': 'phone',
    'mobile': 'phone',
    'date_of_join': 'date_of_joining',
    'joining_date': 'date_of_joining',
    'doj': 'date_of_joining',
    'password': 'user_password',
    'pan_number': 'pan',
    'aadhaar_number': 'aadhaar',
    'aadhar': 'aadhaar',
    'account_no': 'account_number',
    'bank': 'bank_name',
    'ifsc': 'ifsc_code',
    'pf_no': 'pf_account_number'
};

const BulkUploadModal = ({ open, onOpenChange, onSuccess, companyId }) => {
    const [file, setFile] = useState(null);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const downloadTemplate = () => {
        const ws = XLSX.utils.aoa_to_sheet([CSV_HEADER]);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Template');
        XLSX.writeFile(wb, 'employee_bulk_upload_template.xlsx');
    };

    const processFile = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setFile(file);
        setError(null);

        const reader = new FileReader();
        reader.onload = (evt) => {
            try {
                const bstr = evt.target.result;
                const wb = XLSX.read(bstr, { type: 'binary' });
                const wsname = wb.SheetNames[0];
                const ws = wb.Sheets[wsname];
                const rawData = XLSX.utils.sheet_to_json(ws, { header: 1 });

                if (rawData.length < 2) {
                    setError('File is empty or missing data rows');
                    return;
                }

                const rawHeaders = rawData[0];
                const rows = rawData.slice(1);

                const headers = rawHeaders.map(h => {
                    const normalized = h?.toString().trim().toLowerCase().replace(/\s+/g, '_');
                    return HEADER_MAPPING[normalized] || normalized;
                });

                const processedData = rows.map((row, index) => {
                    const obj = { _id: index };
                    headers.forEach((h, i) => {
                        if (!h) return;
                        let val = row[i];

                        if (val === undefined || val === null) {
                            val = '';
                        }

                        if (h === 'date_of_joining') {
                            if (typeof val === 'number') {
                                // excel date serial
                                const date = new Date(Math.round((val - 25569) * 86400 * 1000));
                                val = date.toISOString().split('T')[0];
                            } else if (typeof val === 'string' && val.trim() !== '') {
                                const date = new Date(val);
                                if (!isNaN(date.getTime())) {
                                    val = date.toISOString().split('T')[0];
                                } else {
                                    val = String(val).trim();
                                }
                            } else {
                                val = '';
                            }
                        } else {
                            // Convert all other fields to string
                            val = String(val).trim();
                        }

                        if (val !== '') {
                            obj[h] = val;
                        }
                    });
                    return obj;
                }).filter(row => Object.keys(row).length > 1); // filter out empty rows

                setData(processedData);
            } catch (err) {
                console.error(err);
                setError('Failed to parse file. Please ensure it is a valid Excel or CSV file.');
            }
        };
        reader.readAsBinaryString(file);
    };

    const handleUpload = async () => {
        if (data.length === 0) return;

        setLoading(true);
        try {
            const payload = data.map(({ _id, account_number, bank_name, ifsc_code, branch, ...rest }) => ({
                ...rest,
                bank_details: (account_number || bank_name || ifsc_code || branch) ? {
                    account_number: account_number || '',
                    bank_name: bank_name || '',
                    ifsc_code: ifsc_code || '',
                    branch: branch || ''
                } : undefined,
                employment_type: rest.employment_type || 'FULL_TIME',
                status: rest.status || 'ACTIVE',
                company_id: companyId
            }));

            await employeeAPI.bulkCreateEmployees(payload);
            toast.success(`Successfully uploaded ${data.length} employees`);
            onOpenChange(false);
            if (onSuccess) onSuccess();
            // Reset state
            setFile(null);
            setData([]);
        } catch (error) {
            console.error(error);
            const detail = error.response?.data?.detail;
            let errorMessage = "Failed to upload employees";

            if (typeof detail === 'string') {
                errorMessage = detail;
            } else if (Array.isArray(detail)) {
                errorMessage = detail.map(e => `${e.loc?.join('.') || 'Error'}: ${e.msg}`).join('\n');
            } else if (typeof detail === 'object' && detail !== null) {
                errorMessage = JSON.stringify(detail);
            }

            setError(errorMessage);
            toast.error("Upload failed. Check error details.");
        } finally {
            setLoading(false);
        }
    };

    const removeRow = (id) => {
        setData(data.filter(row => row._id !== id));
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-5xl max-h-[90vh] flex flex-col p-0">
                <DialogHeader className="p-6 pb-0">
                    <DialogTitle>Bulk Upload Employees</DialogTitle>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto p-6 space-y-4">
                    {!file ? (
                        <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-12 text-center space-y-4">
                            <div className="flex justify-center">
                                <div className="p-4 bg-primary/10 rounded-full">
                                    <Upload className="h-8 w-8 text-primary" />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <h3 className="font-semibold text-lg">Upload Excel or CSV</h3>
                                <p className="text-muted-foreground max-w-xs mx-auto">
                                    Drag and drop your file here or click to browse. Use our template for best results.
                                </p>
                            </div>
                            <Input
                                type="file"
                                accept=".xlsx, .xls, .csv"
                                className="hidden"
                                id="file-upload"
                                onChange={processFile}
                            />
                            <Button asChild>
                                <label htmlFor="file-upload" className="cursor-pointer">
                                    Select File
                                </label>
                            </Button>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div className="flex items-center justify-between bg-muted/50 p-3 rounded-lg border">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-background rounded-md border">
                                        <FileDown className="h-5 w-5 text-primary" />
                                    </div>
                                    <div>
                                        <p className="font-medium">{file.name}</p>
                                        <p className="text-xs text-muted-foreground">{(file.size / 1024).toFixed(1)} KB • {data.length} rows detected</p>
                                    </div>
                                </div>
                                <Button variant="ghost" size="icon" onClick={() => { setFile(null); setData([]); setError(null); }}>
                                    <X className="h-4 w-4" />
                                </Button>
                            </div>

                            {error && (
                                <Alert variant="destructive">
                                    <AlertCircle className="h-4 w-4" />
                                    <AlertDescription className="whitespace-pre-wrap">{error}</AlertDescription>
                                </Alert>
                            )}

                            <div className="border rounded-md overflow-hidden">
                                <Table>
                                    <TableHeader className="bg-muted/50">
                                        <TableRow>
                                            <TableHead>Code</TableHead>
                                            <TableHead>First Name</TableHead>
                                            <TableHead>Last Name</TableHead>
                                            <TableHead>Email</TableHead>
                                            <TableHead>Joining Date</TableHead>
                                            <TableHead className="w-[50px]"></TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {data.slice(0, 10).map((row) => (
                                            <TableRow key={row._id}>
                                                <TableCell className="font-mono text-xs">{row.employee_code}</TableCell>
                                                <TableCell>{row.first_name}</TableCell>
                                                <TableCell>{row.last_name}</TableCell>
                                                <TableCell className="text-xs">{row.email}</TableCell>
                                                <TableCell>{row.date_of_joining}</TableCell>
                                                <TableCell>
                                                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeRow(row._id)}>
                                                        <X className="h-3 w-3" />
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                                {data.length > 10 && (
                                    <div className="p-2 text-center text-xs text-muted-foreground border-t bg-muted/20">
                                        Showing first 10 rows of {data.length}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                <DialogFooter className="p-6 pt-0 flex flex-row items-center justify-between border-t mt-4 pt-6 w-full sm:justify-between">
                    <Button variant="outline" onClick={downloadTemplate} className="flex items-center gap-2">
                        <FileDown className="h-4 w-4" />
                        Download Template
                    </Button>
                    <div className="flex items-center gap-2">
                        <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
                            Cancel
                        </Button>
                        <Button onClick={handleUpload} disabled={loading || data.length === 0}>
                            {loading ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Uploading...
                                </>
                            ) : (
                                `Upload ${data.length} Employees`
                            )}
                        </Button>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default BulkUploadModal;
